/**
 * KPI Shared State - 공유 상태 관리
 *
 * @description 여러 store에서 공통으로 사용하는 상태
 *              순환 의존성 방지를 위해 별도 모듈로 분리
 *
 * @usage
 * import { kpiList, selectedKpi, ... } from './useKpiSharedState';
 */
import { ref, computed } from 'vue';
import { useCommonStore } from '~/stores/useCommonStore';
import type {
  SelectOption,
  MemberOption,
  KpiItem,
  KpiStatusItem,
  MemberTreeItem,
  LoginUserInfo,
  MonthlyRecord,
  TaskRecord,
  OkrRecord,
  AgreementData,
  AgreementHistoryItem,
} from './useKpiTypes';

// ============================================
// 검색 조건 (applied - 실제 API 조회에 사용되는 확정 값)
// 검색 버튼(돋보기) 클릭 또는 액션 버튼 클릭 시에만 갱신됨
// ============================================
const currentYear = new Date().getFullYear().toString();

// 공통 store 지연 조회 (모듈 로드 시점엔 pinia가 아직 준비 안 됨)
const getCommonStoreForYear = () => {
  const { $pinia } = useNuxtApp();
  return useCommonStore($pinia);
};

// selectedYear (applied): API 조회에 쓰이는 확정 값 (검색 버튼 클릭 시점에만 변경)
// → commonStore.findYear 와 직접 묶지 않아야 watch(selectedYear) 가 셀렉트 변경마다 트리거되지 않음
// → 초기값/메뉴 이동 시 동기화는 useKpiStore.initialize() 에서 commonStore.findYear 로부터 복원
export const selectedYear = ref<string>(currentYear);
export const selectedDept = ref<string>('');
export const selectedMember = ref<string>('');
export const activeMember = ref<string>('');

// ============================================
// 검색 조건 (draft - 사용자가 UI에서 선택 중인 임시 값)
// 셀렉트/패널 변경 시 즉시 반영되지만 API 조회는 안 함
// 검색 버튼 클릭 시 → applied(selected*)로 복사됨
// ============================================
// draftYear: 공통 store.findYear 와 양방향 바인딩
// → KPI 화면에서 셀렉트 변경 시 즉시 commonStore.findYear 갱신 → 다른 메뉴 이동 시 기준년도 유지
// → selectedYear 와는 분리되어 있어 검색 버튼 클릭 전까지 API 조회는 트리거되지 않음
export const draftYear = computed<string>({
  get: () => getCommonStoreForYear().findYear ?? currentYear,
  set: (v: string) => {
    getCommonStoreForYear().$patch({ findYear: v });
  },
});
export const draftDept = ref<string>('');
export const draftMember = ref<string>('');
export const draftGoalTypeFilter = ref<string>('all');

// ============================================
// 목표 유형 필터 (applied - 'all' | 'kpi' | 'okr')
// ============================================
export const goalTypeFilter = ref<string>('all');

// ============================================
// 리더(직책에 '장' 포함) 여부
// ============================================
export const isLeader = ref(false);

// ============================================
// 콤보박스 옵션
// ============================================
export const yearOptions = ref<SelectOption[]>([]);
export const deptOptions = ref<SelectOption[]>([]);
export const memberOptions = ref<MemberOption[]>([]);

// ============================================
// 로그인 사용자 정보
// ============================================
export const loginUserInfo = ref<LoginUserInfo | null>(null);

// ============================================
// MemberPanel 트리
// ============================================
export const memberTree = ref<MemberTreeItem[]>([]);

// ============================================
// MemberPanel 구성원/공유자 목록 (탭용)
// ============================================
// agreementCd/agreementNm: 구성원(team) 탭 합의상태 배지 표시용 (shareUserList.do가 함께 내려줌)
// kpi/okr: 이 사용자가 "어느 도메인"에서 나를 매니저/공유자로 등록했는지(열람 권한 도메인)
//   → KPI 공유자만이면 {kpi:true}, OKR 공유자만이면 {okr:true}, 양쪽이면 둘 다 true
//   → 통합 화면에서 KPI↔OKR 교차 열람 누수를 막기 위한 도메인 차원
export const memberShareList = ref<
  {
    searchUserId: string;
    searchUserNm: string;
    posNm: string;
    deptNm: string;
    agreementCd?: string;
    agreementNm?: string;
    kpi?: boolean;
    okr?: boolean;
  }[]
>([]);

// 나를 매니저로 등록한 구성원(team) 캐시 — 조직탭/검색에서 accessType 판별용
export const teamMemberCache = ref<
  {
    searchUserId: string;
    searchUserNm: string;
    posNm: string;
    deptNm: string;
    agreementCd?: string;
    agreementNm?: string;
    kpi?: boolean;
    okr?: boolean;
  }[]
>([]);

// ============================================
// KPI 합의(Agreement) 기능 상태
// ============================================

/**
 * KPI 합의 기능 사용 여부 (설정(confMng)에서 결정되는 플래그)
 *
 * - **백엔드가 'Y'/'N' 문자열로 내려주는 값**을 프론트에서 boolean으로 변환해 보관합니다.
 * - 이 값이 false면, 합의 UI/버튼/이력은 전부 숨기고 기존 KPI 화면처럼 동작합니다.
 */
export const agreementYn = ref<boolean>(false);

/**
 * 현재 대상 사용자(내 KPI 또는 구성원 KPI)의 "합의 상태" 요약 정보
 *
 * - AS-IS에서는 `kpiList.do` 호출 시 agreementYn==Y면 함께 내려옵니다.
 * - 예: agreementCd='001'(합의요청), '003'(합의완료), '004'(반려), '005'(합의취소) 등
 */
export const agreementData = ref<AgreementData | null>(null);

/** 합의 이력 목록 */
export const agreementHistoryList = ref<AgreementHistoryItem[]>([]);

/**
 * 합의요청 전 검증용: 대상 사용자의 매니저 수
 *
 * - AS-IS: kpiList.do 응답의 managerCnt 사용
 * - managerCnt==0이면 합의요청을 막고 "매니저 설정"을 유도
 */
export const managerCnt = ref<number>(0);

/**
 * KPI 등록/수정 기간 여부 (기간 만료 시 false)
 *
 * - AS-IS: kpi.do 응답의 kpiConfigTermYn('Y'/'N')을 프론트에서 boolean으로 변환
 * - 기간이 만료되면 KPI 저장/수정/삭제 + 합의 버튼도 모두 차단해야 합니다.
 */
export const kpiConfigTermYn = ref<boolean>(true);

/**
 * agreementEditYn(합의 완료 전 수정 가능 여부)
 *
 * ⚠️ 매우 중요(AS-IS 동작 그대로)
 * - 백엔드가 내려주지 않기 때문에 프론트에서 agreementCd로 파생합니다.
 * - agreementCd가 아래 상태면 KPI 기본정보 수정이 막힙니다.\n
 *   - '001' 합의요청 상태\n
 *   - '003' 합의완료 상태\n
 * - 그 외(없음/002/004/005 등)에서는 수정 가능\n
 */
export const agreementEditYn = computed<boolean>(() => {
  if (!agreementYn.value) return true;
  const cd = agreementData.value?.agreementCd;
  return cd !== '001' && cd !== '003';
});

/**
 * 현재 "어떤 경로"로 사용자를 선택했는지(= 매니저 버튼 노출 기준)
 *
 * 요구사항(사용자 선택):\n
 * - MemberPanel의 **구성원(team) 탭에서 선택한 사용자일 때만** 매니저로 간주\n
 * - 단, 관리자는 항상 매니저로 간주\n
 *
 * 그래서 selectionContext를 저장해두고, PageHeader에서 `isAgreementManager`를 계산합니다.
 */
export type SelectedMemberAccessType = 'self' | 'org' | 'team' | 'share';
export const selectedMemberAccessType = ref<SelectedMemberAccessType>('self');

// ============================================
// 선택 대상자의 "열람 허용 도메인" (KPI↔OKR 교차 열람 누수 방지)
// ============================================
/**
 * 현재 선택된 대상자에 대해 로그인 사용자가 열람 가능한 도메인
 *
 * ⚠️ 매우 중요 — KPI/OKR 메뉴 통합 후 누수 차단의 핵심
 * - 본인 / 조직탭 / 리더(헤더검색) / 관리자 → {kpi:true, okr:true} (양쪽 허용)
 * - 구성원(team)·공유(share) 탭 선택 → 그 대상자가 나를 등록한 도메인만 허용
 *   (예: KPI 공유자로만 등록 → {kpi:true, okr:false} → OKR 조회/노출 차단)
 * - 기본값은 본인 기준 양쪽 허용
 */
export const selectedMemberDomains = ref<{ kpi: boolean; okr: boolean }>({
  kpi: true,
  okr: true,
});

/**
 * 현재 대상자를 "어느 경로로" 선택했는지 (열람 도메인 제한 판정 전용)
 *
 * ⚠️ selectedMemberAccessType 과 분리해서 쓴다 — 용도가 다르다.
 * - selectedMemberAccessType : 합의(Agreement) 매니저 버튼 노출 판정용.
 *   헤더 검색 때마다 team 캐시 기준으로 재계산되므로 "선택 경로"가 보존되지 않는다.
 *   (조직탭에서 고른 사람이 team 캐시에 있으면 team 으로 바뀜)
 * - selectedMemberDomainScope : 도메인 제한 판정용. 선택 경로를 그대로 보존한다.
 *   'open'  = 본인/조직탭/리더/관리자 → KPI·OKR 양쪽 허용
 *   'team'  = 구성원 탭에서 선택 → 그 대상자가 나를 등록한 도메인만 허용
 *   'share' = 공유 탭에서 선택   → 위와 동일
 *
 * 이 둘을 하나로 합치면, 조직탭에서 고른 사용자가 team 캐시에 있다는 이유로
 * 두 번째 검색부터 열람이 축소되는 반대 방향 버그가 생긴다.
 *
 * ⚠️ scope 는 반드시 "그 scope 를 기록한 대상자 userId" 와 함께 보관한다.
 * 헤더의 구성원 셀렉트(리더 전용)로 다른 사람을 고르면 scope 를 초기화할 마땅한
 * 지점이 없어서, id 를 묶어두지 않으면 이전 대상자의 제한이 엉뚱한 사람에게 적용된다.
 * (반대 방향 버그 — 리더가 자기 팀원을 못 보게 되는 과차단)
 */
export const selectedMemberDomainScope = ref<{ scope: 'open' | 'team' | 'share'; userId: string }>({
  scope: 'open',
  userId: '',
});

const GOAL_TYPE_ALL_OPTIONS = [
  { label: '전체', value: 'all' },
  { label: 'KPI', value: 'kpi' },
  { label: 'OKR', value: 'okr' },
];

/**
 * 선택 대상자의 허용 도메인에 따라 노출할 "목표유형" 셀렉트 옵션
 * - 양쪽 허용 → 전체/KPI/OKR
 * - KPI만 → KPI
 * - OKR만 → OKR
 * - 둘 다 불가 → 빈 목록
 *
 * ⚠️ "둘 다 불가"에서 전체를 노출하면 안 된다.
 * 해당 연도에 나에게 아무것도 공유/위임하지 않은 대상자를 보고 있다는 뜻이므로,
 * 여기서 전체를 열어주면 데이터는 비어 있는데 셀렉트만 전부 열려 누수처럼 보인다.
 * (데이터 fetch 는 canKpi/canOkr 로 이미 차단되지만, 화면 표시도 같은 판단을 따라야 한다)
 */
export const allowedGoalTypeOptions = computed(() => {
  const { kpi, okr } = selectedMemberDomains.value;
  if (kpi && okr) return GOAL_TYPE_ALL_OPTIONS;
  if (kpi) return GOAL_TYPE_ALL_OPTIONS.filter(o => o.value === 'kpi');
  if (okr) return GOAL_TYPE_ALL_OPTIONS.filter(o => o.value === 'okr');
  return [];
});

/**
 * 선택 대상자의 허용 도메인에 맞춰 목표유형 필터(draft/applied)를 강제 보정
 * - 허용되지 않은 값이 선택돼 있으면 허용된 첫 번째 값으로 교체
 * - 한쪽 도메인만 허용될 때 'all'은 양쪽을 노출하므로 허용 목록에서 제외됨
 * - selectedMemberDomains 가 갱신된 직후(대상 선택 시점) 호출한다
 */
export const enforceGoalTypeForDomains = () => {
  const allowedValues = allowedGoalTypeOptions.value.map(o => o.value);
  if (allowedValues.length === 0) return;
  if (!allowedValues.includes(draftGoalTypeFilter.value)) {
    draftGoalTypeFilter.value = allowedValues[0];
  }
  if (!allowedValues.includes(goalTypeFilter.value)) {
    goalTypeFilter.value = allowedValues[0];
  }
};

// ============================================
// KPI 상태 목록
// ============================================
export const kpiStatusList = ref<KpiStatusItem[]>([]);

// ============================================
// KPI 목록 (여러 store에서 공유)
// ============================================
export const kpiList = ref<KpiItem[]>([]);
export const isLoading = ref(false);

// ============================================
// 헤더 배지 카운트
// ============================================
export const pendingCount = ref(0);

// ============================================
// 실적상세 패널
// ============================================
export const selectedKpi = ref<KpiItem | null>(null);
export type DetailTabType = 'monthly' | 'task' | 'okr';
export const activeDetailTab = ref<DetailTabType>('monthly');
export const monthlyRecords = ref<MonthlyRecord[]>([]);
export const targetHistoryList = ref<any[]>([]);
export const taskRecords = ref<TaskRecord[]>([]);
export const okrRecords = ref<OkrRecord[]>([]);
export const detailLoading = ref({ monthly: false, task: false, okr: false });
export const detailLoaded = ref({ monthly: false, task: false, okr: false });

// ============================================
// 실적상세 패널 - 활동 탭 (연결 업무 목록)
// ============================================
export const kpiActivityPlanList = ref<any[]>([]);
export const kpiActivityLoading = ref(false);

// ============================================
// KPI 피드백 관련
// ============================================
export const feedbackMonList = ref<string[]>([]); // KPI 피드백 등록 가능 월 목록

// ============================================
// 멤버 패널 점수 캐시 무효화 트리거
// ============================================
export const memberScoreVersion = ref(0);

// ============================================
// 상세 데이터 초기화 함수
// ============================================
export const resetDetailData = () => {
  monthlyRecords.value = [];
  targetHistoryList.value = [];
  taskRecords.value = [];
  okrRecords.value = [];
  detailLoaded.value = { monthly: false, task: false, okr: false };
  kpiActivityPlanList.value = [];
};

// ============================================
// draft → applied 복사 (검색 버튼/액션 클릭 시 호출)
// ============================================
export const applySearchConditions = () => {
  selectedYear.value = draftYear.value; // 연도
  selectedDept.value = draftDept.value; // 부서
  selectedMember.value = draftMember.value; // 구성원
  goalTypeFilter.value = draftGoalTypeFilter.value; // 목표유형
};

// ============================================
// applied → draft 복사 (분기 변경 등 검색 없이 조회 시, 표시값을 실제값으로 동기화)
// ============================================
export const revertDraftToApplied = () => {
  draftYear.value = selectedYear.value;
  draftDept.value = selectedDept.value;
  draftMember.value = selectedMember.value;
  draftGoalTypeFilter.value = goalTypeFilter.value;
};

// ============================================
// 상태 초기화 (전체 - draft와 applied 모두 리셋)
// ============================================
export const resetAllState = () => {
  // applied
  selectedYear.value = currentYear;
  selectedDept.value = '';
  selectedMember.value = '';
  activeMember.value = '';
  // draft (applied와 동기화)
  draftYear.value = currentYear;
  draftDept.value = '';
  draftMember.value = '';
  draftGoalTypeFilter.value = 'all';
  goalTypeFilter.value = 'all';

  selectedKpi.value = null;
  activeDetailTab.value = 'monthly';
  kpiList.value = [];
  memberTree.value = [];
  resetDetailData();
};
